import personnelApi from '../api/personnelApi';
import baseApi from '../api/baseApi';

export default {
    namespaced: true,
    state: {
        loadingPlants: false,
        loadingPersonnel: false,
        loadingLevels: false,
        loadingHistory: false,
        loadingHistoryExport: false,
        savingUpdates: false,
        saveError: "",
        showPersonnelInOpenAttendanceError: false,
        loadingHistoryExportError: "",
        plantsWithTeams: {},
        employeeStatuses: [
            {
                Name: "Active",
                Value: 0
            },
            {
                Name: "Terminated",
                Value: 1
            }
        ],
        personnel: [],
        personnelTotal: 0,
        levels: [],
        individualUpdateData: {},
        personnelMovementHistory: [],
        personnelMovementHistoryTotal: 0,
        personnelMovementHistoryExport: null
    },
    getters: {
        loading(state) {
            return state.loadingPersonnel || state.loadingPlants || state.loadingLevels;
        },
        loadingPersonnelHistory(state) {
            return state.loadingPlants || state.loadingLevels || state.loadingHistory;
        },
        getPlantsWithTeamsSortedArray(state) {
            return Object.keys(state.plantsWithTeams).map(function(key) {
                return state.plantsWithTeams[key];
            }).sort(function(a, b) {
                let x = a.PlantName.toLowerCase();
                let y = b.PlantName.toLowerCase();
                if (x < y) { return -1; }
                if (x > y) { return 1; }
                return 0;
            });
        },
        getLevelByID(state) {
            return function(id) {
                let level = state.levels.find(l => l.RoleTypeID === id);

                if (!id || !level) {
                    return {
                        LevelName: ""
                    };
                }

                return level;
            };
        },
        getTeamsForPlant(state) {
            return function(plantID) {
                let teams = [];

                let plant = state.plantsWithTeams[plantID];

                if (plant && plant.Teams) {
                    teams = plant.Teams.slice(); // we have to slice (aka copy) it, otherwise when we sort it, it mutates the original array and causes an infinite loop because it's reactive
                }

                teams.sort(function(a, b) {
                    let x = a.Name.toLowerCase();
                    let y = b.Name.toLowerCase();
                    if (x < y) { return -1; }
                    if (x > y) { return 1; }
                    return 0;
                });

                return teams;
            };
        },
        getLevelsForPlant(state) {
            return function (plantID) {
                let plant = state.plantsWithTeams[plantID];
                if(plant) {
                    return state.levels.filter(l=> l.CompanyID == plant.CompanyID);
                } else {
                    return [];
                }
                
            };
        },
        getPlantByID(state) {
            return function(plantID) {
                let plant = state.plantsWithTeams[plantID];

                // return a plant with an empty name (just to avoid null reference issues)
                if (!plantID || !plant) {
                    return {
                        PlantName: ""
                    };
                }

                return plant;
            };
        },
        getTeamByPlantAndID(state) {
            return function(plantID, teamID) {
                let plant = state.plantsWithTeams[plantID];

                if (!plantID || !plant || !plant.Teams) {
                    return {
                        Name: ""
                    };
                }

                let team = plant.Teams.find(team => team.TeamID === teamID);

                if (!teamID || !team) {
                    return {
                        Name: ""
                    };
                }

                return team;
            };
        }
    },
    mutations: {
        updatePersonnel(state, value) {
            state.personnel = value;
        },
        updatePersonnelHistoryExport(state, value) {
            state.personnelMovementHistoryExport = value;
        },
        updateHistory(state, value) {
            state.personnelMovementHistory = value;
        },
        updatePersonnelTotal(state, value) {
            state.personnelTotal = value;
        },
        updatePersonnelMovementHistoryTotal(state, value) {
            state.personnelMovementHistoryTotal = value;
        },
        updatePlants(state, value) {
            state.plantsWithTeams = value;
        },
        updateLevels(state, value) {
            state.levels = value;
        },
        setLoadingPersonnel(state, value) {
            state.loadingPersonnel = value;
        },
        setLoadingPlants(state, value) {
            state.loadingPlants = value;
        },
        setLoadingLevels(state, value) {
            state.loadingLevels = value;
        },
        setLoadingHistory(state, value) {
            state.loadingHistory = value;
        },
        setLoadingHistoryExport(state, value) {
            state.loadingHistoryExport = value;
        },
        setShowPersonnelInOpenAttendanceError(state, value) {
            state.showPersonnelInOpenAttendanceError = value;
        },
        setSavingUpdates(state, value) {
            state.savingUpdates = value;

            // clear save error when we are saving again
            if (value) {
                state.saveError = "";
            }
        },
        setSaveError(state, value) {
            state.saveError = value;
        },
        setLoadingHistoryExportError(state, value) {
            state.loadingHistoryExportError = value;
        },
        setIndividualUpdate(state, value) {
            // overwrite any previous data that was here
            state.individualUpdateData[value.id] = value.newData;

            // delete data if it is empty
            if (!value.newData.newPlantID && !value.newData.newTeamID && !value.newData.newLevelID) {
                delete state.individualUpdateData[value.id];
            }
        },
        clearIndividualUpdates(state) {
            state.individualUpdateData = {};
        }
    },
    actions: {
        hasIndividualUpdateData(context) {
            return new Promise((resolve) => {
                if (!context.state.individualUpdateData) {
                    resolve(false);
                }
    
                // loop through individual update data
                // at least one of them has to exist and also have a value in it
                let arrayOfValues = Object.values(context.state.individualUpdateData);
                for (let i=0; i<arrayOfValues.length; i++) {
                    let value = arrayOfValues[i];
                    
                    if (value.newPlantID && value.newPlantID > 0) {
                        resolve(true);
                    }
    
                    if (value.newTeamID && value.newTeamID > 0) {
                        resolve(true);
                    }
    
                    if (value.newLevelID && value.newLevelID > 0) {
                        resolve(true);
                    }
                }
    
                resolve(false);
            });
        },
        isConflictBetweenBulkAndIndividual(context, bulk) {
            return new Promise((resolve) => {
                // can't be a conflict if there is no individual data
                if (!context.state.individualUpdateData) {
                    resolve(false);
                }

                // loop through individual data
                let arrayOfValues = Object.values(context.state.individualUpdateData);
                for (let i=0; i<arrayOfValues.length; i++) {
                    let value = arrayOfValues[i];

                    if (bulk.plantID && bulk.plantID > 0) {
                        // there is a conflict if we've selected an individual plant and a bulk plant
                        if (value.newPlantID && value.newPlantID > 0) {
                            resolve(true);
                        }

                        // there is also a conflict if we've selected an individual team and a bulk plant, because the team selection assumes that the plant is staying the same
                        if (value.newTeamID && value.newTeamID > 0) {
                            resolve(true);
                        }
                    }

                    if (bulk.teamID && bulk.teamID > 0) {
                        // there is a conflict if we've selected any individual teams
                        if (value.newTeamID && value.newTeamID > 0) {
                            resolve(true);
                        }

                        // note that if we have selected a bulk team, we have definitely selected a bulk plant anyway, so this check is superfluous (but left in for understanding)
                    }

                    if (bulk.levelID && bulk.levelID > 0) {
                        // there is a conflict if we've selected an individual level and a bulk level
                        if (value.newLevelID && value.newLevelID > 0) {
                            resolve(true);
                        }
                    }
                }

                resolve(false);
            });
        },
        async workTypesMatch(context, bulk) {
            // check bulk values
            if (bulk.teamID && bulk.teamID > 0 && bulk.levelID && bulk.levelID > 0) {
                let team = context.getters.getTeamByPlantAndID(bulk.plantID, bulk.teamID);
                let level = context.getters.getLevelByID(bulk.levelID);

                if (team.WorkTypeID !== level.WorkTypeID) {
                    return false;
                }
            }                

            // loop through individual data
            let arrayOfIds = Object.keys(context.state.individualUpdateData);
            for (let i=0; i<arrayOfIds.length; i++) {
                let id = arrayOfIds[i];
                let value = context.state.individualUpdateData[id];                

                if ((value.newTeamID && value.newTeamID > 0) || (value.newLevelID && value.newLevelID > 0)) {                    
                    // check individual values
                    let personnel = context.state.personnel.find(p => p.PersonnelID == id);
                    let team = context.getters.getTeamByPlantAndID(value.newPlantID || personnel.PrimaryPlantID, value.newTeamID || personnel.DefaultTeamId);
                    let level = context.getters.getLevelByID(value.newLevelID || personnel.DefaultRoleId);

                    if (team.WorkTypeID !== level.WorkTypeID) {
                        return false;
                    }
                }
            }

            return true;
        },
        async saveUpdates(context, payload) {
            context.commit("setSavingUpdates", true);

            // turn the individual updates into an array instead of an object
            let individualUpdatesArray = Object.keys(context.state.individualUpdateData)
                .map(function(key) {
                    return {
                        id: key,
                        newData: context.state.individualUpdateData[key]
                    };
                });

            let data = {
                bulk: payload.bulk,
                individual: individualUpdatesArray,
                search: payload.search
            };

            try {
                //clear error before saving
                context.commit("setShowPersonnelInOpenAttendanceError", false)

                let allPersonnelUpdated = await personnelApi.savePersonnelMovementUpdates(data);
                if(!allPersonnelUpdated){
                    context.commit("setShowPersonnelInOpenAttendanceError", true)
                }
            }
            catch(err) {
                context.commit("setSaveError", await baseApi.parseHttpRequestError(err));
            }
            finally {
                context.commit("setSavingUpdates", false);
            }
        },
        async getLevels({ commit }) {
            commit("setLoadingLevels", true);
            commit("updateLevels", []);
            
            try {
                let data = await personnelApi.getRoleTypes();

                commit("updateLevels", data);
            }
            finally {
                commit("setLoadingLevels", false);
            }
        },
        async getPlants({ commit }) {
            commit("setLoadingPlants", true);
            commit("updatePlants", []);
            
            try {
                let data = await personnelApi.getPlantsWithTeams();

                commit("updatePlants", data);
            }
            finally {
                commit("setLoadingPlants", false);
            }
        },
        async getPersonnel({ commit }, params) {
            commit("setLoadingPersonnel", true);
            commit("updatePersonnel", []);

            // if we are refiltering or changing pages, wipe any data that we entered in the grid
            commit("clearIndividualUpdates");
            
            try {
                let data = await personnelApi.getPersonnel(params);

                commit("updatePersonnel", data.Personnel);
                commit("updatePersonnelTotal", data.Total);
            }
            finally {
                commit("setLoadingPersonnel", false);
            }
        },
        async getHistory(context, payload) {
            context.commit("setLoadingHistory", true);
            context.commit("updateHistory", []);

            try {
                let data = await personnelApi.getMovementsHistory(payload);

                context.commit("updateHistory", data.Movements);
                context.commit("updatePersonnelMovementHistoryTotal", data.Total);
            }
            finally {
                context.commit("setLoadingHistory", false);
            }
        },
        async getHistoryForExport(context, payload) {
            context.commit("setLoadingHistoryExport", true);
            context.commit("setLoadingHistoryExportError", "");
            context.commit("updatePersonnelHistoryExport", {});

            try {
                let data = await personnelApi.getMovementsHistoryForExport(payload);

                context.commit("updatePersonnelHistoryExport", data);
            }
            catch(err) {
                context.commit("setLoadingHistoryExportError", await baseApi.parseHttpRequestError(err));
            }
            finally {
                context.commit("setLoadingHistoryExport", false);
            }
        },
        clearSaveError(context) {
            context.commit("setSaveError", "");
        }
    }
}